/* eslint-disable max-len */
import React from 'react';
import { createUseStyles } from 'react-jss';
import { Stack, StackItem, DefaultButton } from '@fluentui/react';
import classNames from 'classnames';
import { map } from 'underscore';
import { PlaygroundTheme } from '../../Theme';
import { Topic, getTopicPrompt } from '../DeveloperPanel/userModeSetting';
import { useLLMSetting } from '../Contexts';
import { useLogger } from '../Logger';

import { TopicCard } from './topics-card';

import EmotionalWellbeingPic from './pic/EmotionalWellbeing.jpg';
// import LoveRelationshipPic from './pic/LoveRelationship.jpg';
// import ParentingChallengesPic from './pic/ParentingChallenges.jpg';
// import HealthFitnessPic from './pic/HealthFitness.jpg';
import WorkplaceCareerPic from './pic/WorkplaceCareer.jpg';
// import InterestsHobbiesPic from './pic/InterestsHobbies.jpg';

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  welcomeTopicsPageContainer: {
    color: theme.themePrimary,
    fontFamily: 'Segoe serif',
    overflowY: 'auto',
    justifyContent: 'unset',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& .cnLanguage': {
      fontFamily: 'Microsoft YaHei',
    },

    '& .title': {
      fontSize: '48px',
      lineHeight: '60px',
      fontWeight: 600,
      marginBottom: '16px',
      whiteSpace: 'nowrap',
      '@media (width > 600px)': {
        '& div': {
          display: 'inline-block',
        },
      },
      '@media (width <= 600px)': {
        fontSize: '24px',
        whiteSpace: 'unset',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        lineHeight: '30px',
      },
    },

    '& .subTitle': {
      fontSize: '20px',
      lineHeight: '30px',
      fontWeight: 400,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      '@media (width > 600px)': {
        '& div': {
          display: 'inline-block',
        },
      },
      '@media (width <= 600px)': {
        whiteSpace: 'unset',
        fontSize: '15px',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
      },
    },
    '& .subTitle + .subTitle': {
      marginTop: 8,
    },

    '& .topicGrid': {
      display: 'grid',
      'grid-template-columns': 'repeat(2, 1fr)',
      gap: 20,
      // 'grid-auto-rows': 'minmax(100px, auto)',
      margin: '0 5vh 10vh',
      '@media (width <= 600px)': {
        'grid-template-columns': 'repeat(1, 1fr)',
      },
    },
    '& .customButtonSection': {
      bottom: '0',
      height: '120px',
      position: 'fixed',
      background: 'linear-gradient(180deg, rgba(255, 244, 228, 0) 0%, #FFF5E6 40.72%)',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      '@media (width <= 600px)': {
        height: '100px',
        opacity: 0.9,
      },
    },
    '& .customButton': {
      marginTop: '40px',
      height: '44px',
      borderRadius: '170px',
      background: theme.background,
      borderColor: '#D6D6D6',
      padding: '16px 24px',
      transition: '300ms',
      '&:hover': {
        // 300ms var(--curveDecelerateMid),box-shadow 166ms linear
        transform: 'scale(1.05)',
      },
      '& .ms-Button-label, .ms-Button-description': {
        color: theme.themePrimary,
        fontSize: '14px',
        lineHeight: '18px',
        textAlign: 'center',
      },
      '@media (width <= 600px)': {
        marginTop: '20px',
      },
    },
  },
}));

export const TopicList = {
  [Topic.EmotionalWellbeing]: {
    // text: 'Emotional Well-being',
    text: 'Feeling a bit down?',
    cnText: '感觉不太开心吗？',
    imgsrc: EmotionalWellbeingPic,
  },
  [Topic.WorkplaceCareer]: {
    // text: 'Workplace & Career',
    text: 'Always Procrastinate?',
    cnText: '向拖延症说拜拜',
    imgsrc: WorkplaceCareerPic,
  },
};

interface IWelcomeTopicsPageProps {
  className?: string;
}

export const WelcomeTopicsPage: React.FC<IWelcomeTopicsPageProps> = ({ className }) => {
  const classes = useStyles();
  const logger = useLogger();
  const { update } = useLLMSetting();
  const title1 = "Hey, I'm Vivara.";
  const title2 = 'Like a best friend, and wiser.';
  const subTitle1 = "Together, we'll empower you to find your best in life—";
  const subTitle2 = 'relationships, personal growth, and overall well-being.';
  const subTitleCn = '我将陪伴你，一起探索更好的自己。';
  const defaultButtonText = 'Something else / 聊点别的...';

  const onClick = React.useCallback((topic: Topic) => {
    update({
      prompt: getTopicPrompt(topic),
      selectedTopic: topic,
    });
    logger.logInfo(`SelectedTopic: ${topic}`);
  }, [update]);

  return (
    <Stack verticalAlign="center" horizontalAlign="center" className={classNames(classes.welcomeTopicsPageContainer, className)}>
      <StackItem>
        <div className="title">
          <div style={{ whiteSpace: 'nowrap' }}>
            {title1}
            &nbsp;
          </div>
          <div style={{ whiteSpace: 'nowrap' }}>{title2}</div>
        </div>
      </StackItem>
      <StackItem style={{ marginBottom: 24 }}>
        <div className="subTitle">
          <div style={{ whiteSpace: 'nowrap' }}>{subTitle1}</div>
          <div style={{ whiteSpace: 'nowrap' }}>{subTitle2}</div>
        </div>
        <div className="subTitle cnLanguage">{subTitleCn}</div>
      </StackItem>
      <StackItem>
        <div className="topicGrid">
          {map(TopicList, ({
            imgsrc, text, cnText,
          }, key: any) => (
            <TopicCard
              key={key}
              imgsrc={imgsrc}
              text={text}
              cnText={cnText}
              onClick={() => onClick(key)}
            />
          ))}
        </div>
      </StackItem>
      <StackItem className="customButtonSection">
        <DefaultButton
          className="customButton"
          onClick={() => onClick(Topic.Default)}
        >
          {defaultButtonText}
        </DefaultButton>
      </StackItem>
    </Stack>
  );
};

WelcomeTopicsPage.defaultProps = {
};
